// Procurify AP bills for Lab Approvals (server OAuth; wlfwkr-lab-approvals group)
import { authService } from '../auth/authService';

class ProcurifyLabService {
  constructor() {
    this.functionAppUrl =
      'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
  }

  async getAuthenticatedHeaders() {
    const token = await authService.getAccessToken();
    const userInfo = authService.getUserInfo();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-user-id': userInfo?.email || userInfo?.username || 'unknown',
    };
  }

  defaultAssigneeEmail() {
    const userInfo = authService.getUserInfo();
    return (userInfo?.email || userInfo?.username || '').trim();
  }

  /**
   * @param {{ assigneeEmail?: string, page?: number, pageSize?: number, status?: number }} [options]
   */
  async fetchAssignedBills(options = {}) {
    const headers = await this.getAuthenticatedHeaders();
    const params = new URLSearchParams();
    const email = (options.assigneeEmail || this.defaultAssigneeEmail()).trim();
    if (email) {
      params.set('assignee_email', email);
    }
    if (options.page != null) {
      params.set('page', String(options.page));
    }
    if (options.pageSize != null) {
      params.set('page_size', String(options.pageSize));
    }
    if (options.status != null && options.status !== '') {
      params.set('status', String(options.status));
    }
    const qs = params.toString();
    const url = `${this.functionAppUrl}/api/procurify_lab_bills${qs ? `?${qs}` : ''}`;
    const response = await fetch(url, { method: 'GET', headers });
    const text = await response.text();
    let body;
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = { success: false, message: text };
    }
    if (!response.ok) {
      throw new Error(body.message || body.error || `procurify_lab_bills failed (${response.status})`);
    }
    if (body.success === false) {
      throw new Error(body.message || body.error || 'procurify_lab_bills failed');
    }
    return body;
  }

  /**
   * @param {string|number} billId — Procurify bill id or uuid
   */
  async fetchBillPdfBlob(billId) {
    const id = String(billId || '').trim();
    if (!id) {
      throw new Error('bill_id is required');
    }
    const headers = await this.getAuthenticatedHeaders();
    const params = new URLSearchParams({ bill_id: id });
    const url = `${this.functionAppUrl}/api/procurify_bill_document?${params.toString()}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      const errText = await response.text();
      let msg = errText;
      try {
        const j = JSON.parse(errText);
        if (j?.message) msg = j.message;
        else if (j?.error) msg = String(j.error);
      } catch {
        /* keep text */
      }
      throw new Error(msg || `procurify_bill_document failed (${response.status})`);
    }
    const ct = (response.headers.get('content-type') || '').toLowerCase();
    if (ct.includes('application/json')) {
      const j = await response.json();
      throw new Error(j.message || j.error || 'Unexpected JSON from procurify_bill_document');
    }
    return response.blob();
  }

  /** In-app approval URL (Approve/Deny in Procurify UI). */
  getBillApprovalUrl(billUuid) {
    const uid = String(billUuid || '').trim();
    if (!uid) return 'https://selkirkmining.procurify.com/';
    return `https://selkirkmining.procurify.com/bills/approvals/${encodeURIComponent(uid)}`;
  }

  /** Open Procurify bill approval page in a new browser tab. */
  openBillInProcurify(billUuidOrId) {
    const uid = String(billUuidOrId || '').trim();
    if (!uid) return false;
    const url = this.getBillApprovalUrl(uid);
    window.open(url, '_blank', 'noopener,noreferrer');
    return true;
  }

  /** Hash-route fallback for some tenants. */
  getBillReviewUrl(billUuid) {
    const uid = String(billUuid || '').trim();
    if (!uid) return 'https://selkirkmining.procurify.com/';
    return `https://selkirkmining.procurify.com/#/ap/bills/${encodeURIComponent(uid)}`;
  }

  /**
   * @param {string|number} billId — uuid or numeric id
   */
  async fetchBillDetail(billId) {
    const id = String(billId || '').trim();
    if (!id) throw new Error('bill_id is required');
    const headers = await this.getAuthenticatedHeaders();
    const params = new URLSearchParams({ bill_id: id });
    const url = `${this.functionAppUrl}/api/procurify_lab_bills?${params.toString()}`;
    const response = await fetch(url, { method: 'GET', headers });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || body.success === false) {
      throw new Error(body.message || body.error || `procurify bill detail failed (${response.status})`);
    }
    return body.bill || {};
  }

  /**
   * @param {{ billId: string|number, action: 'approve'|'deny', comment?: string }} params
   */
  async approveBill({ billId, action, comment }) {
    const id = String(billId || '').trim();
    if (!id) throw new Error('bill_id is required');
    const act = String(action || '').trim().toLowerCase();
    if (act !== 'approve' && act !== 'deny') {
      throw new Error('action must be approve or deny');
    }
    const headers = await this.getAuthenticatedHeaders();
    const url = `${this.functionAppUrl}/api/procurify_bill_approval`;
    const payload = { bill_id: id, action: act };
    if (comment && String(comment).trim()) {
      payload.comment = String(comment).trim();
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    let body;
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = { success: false, message: text };
    }
    if (!response.ok || body.success === false) {
      throw new Error(body.message || body.error || `procurify_bill_approval failed (${response.status})`);
    }
    return body;
  }
}

export const procurifyLabService = new ProcurifyLabService();
export default procurifyLabService;
